import React, {useCallback, memo, ReactNode} from "react";
import {Stack, Text, View} from "@unibuddy/patron";
import {useAnalytics} from "@unibuddy/tracking";
import {TrackEvents} from "ubcommunity-shared/src/constants";
import {LocalStorageNames} from "ubcommunity-shared/src/constants";
import {useCommunityTheme} from "ubcommunity-shared/src/Theme/CommunityThemeProvider";
import {
    SSO_INIT_STATUS,
    SSO_PROVIDER,
    SSO_FLOWS,
} from "ubcommunity-shared/src/Auth/SSO/auth-ui/types";
import ActivityIndicator from "ubcommunity-shared/src/General/ActivityIndicator/ActivityIndicator";
import {useSSO} from "ubcommunity-shared/src/Auth/SSO/hooks/useSSO";
import {useAsyncLocalStorage} from "ubcommunity-shared/src/Auth/AuthProvider/useAsyncLocalStorage";
import {GoogleAuthButton} from "./GoogleAuthButton";
import {AppleAuthButton} from "./AppleAuthButton";
import {ssoRedirect} from "./auth-ui";
import {useGetUniSlug} from "./utils/useGetUniSlug";

const ErrorText = memo(({message}: {message: string}) => {
    return (
        <View backgroundColor="red100" padding="small-medium" borderRadius="xxsmall" w="100%">
            <Text color="red800">{message}</Text>
        </View>
    );
});

export const SOCIAL_LOGIN_STRINGS = {
    SSO_INIT_ERROR:
        "Oops, we encountered an issue with social login. Please try closing the app then re-opening it.",
    INVALID_EMAIL_ERROR:
        "Oops! Your Google account doesn't match the invited email. Please try again or continue with email.",
    LOADING: "Loading social login providers",
};

export function SocialLogin() {
    const {setValue} = useAsyncLocalStorage(LocalStorageNames.SSO_FLOW, undefined);
    const {trackEvent} = useAnalytics();
    const {getUniSlug} = useGetUniSlug();
    const universitySlug = getUniSlug();
    const {darkModeEnabled} = useCommunityTheme();
    const {ssoStatus, isRedirectUrlMissing} = useSSO();

    const onProviderClick = useCallback(
        (providerName: SSO_PROVIDER) => {
            setValue({
                slug: universitySlug,
                flow: SSO_FLOWS.SOCIAL,
            });
            trackEvent(TrackEvents.SOCIAL_SIGN_ON_CLICKED, {provider: providerName});
            ssoRedirect({ssoProvider: providerName});
        },
        [setValue, universitySlug, trackEvent],
    );

    const renderErrorState = useCallback(() => {
        const Component = <ErrorText message={SOCIAL_LOGIN_STRINGS.SSO_INIT_ERROR} />;
        if (ssoStatus === SSO_INIT_STATUS.UNKNOWN) return null;
        if (ssoStatus === SSO_INIT_STATUS.FAILED) return Component;
        if (ssoStatus === SSO_INIT_STATUS.READY && isRedirectUrlMissing) return Component;
    }, [ssoStatus, isRedirectUrlMissing]);

    // TODO temp - replace with real login flow error
    const hasAuthError = false;

    const renderSSOProviderButtons = useCallback((): ReactNode => {
        if (
            !universitySlug ||
            isRedirectUrlMissing ||
            ssoStatus === SSO_INIT_STATUS.FAILED ||
            ssoStatus === SSO_INIT_STATUS.UNKNOWN
        )
            return null;

        return (
            <Stack space="medium">
                <GoogleAuthButton onClick={() => onProviderClick(SSO_PROVIDER.GOOGLE)} />
                <AppleAuthButton onClick={() => onProviderClick(SSO_PROVIDER.APPLE)} />
            </Stack>
        );
    }, [onProviderClick, universitySlug, ssoStatus, isRedirectUrlMissing]);

    if (ssoStatus === SSO_INIT_STATUS.LOADING)
        return (
            <View w="100%" alignItems="center" justifyContent="center">
                <ActivityIndicator accessibilityLabel={SOCIAL_LOGIN_STRINGS.LOADING} />
            </View>
        );

    return (
        <Stack space="large">
            <Stack space="medium">
                {hasAuthError ? (
                    <ErrorText message={SOCIAL_LOGIN_STRINGS.INVALID_EMAIL_ERROR} />
                ) : null}
                {renderErrorState()}
                {renderSSOProviderButtons()}
            </Stack>
            <View flexDirection="row" alignItems="center" justifyContent="center" w="full">
                <View flexGrow="1" h={1} backgroundColor="grey600" />
                <View paddingX="small" backgroundColor="body">
                    <Text
                        align="center"
                        size="xxsmall"
                        color={darkModeEnabled ? "white" : "grey600"}
                    >
                        OR CONTINUE WITH EMAIL
                    </Text>
                </View>
                <View flexGrow="1" h={1} backgroundColor="grey600" />
            </View>
        </Stack>
    );
}
